import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import { setNotificationCount } from '../../actions/notification';
import PropTypes from 'prop-types';
import Link from '../UI/Link/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import TableFooter from '@mui/material/TableFooter';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import AutoCompleteField from '../UI/AutoCompleteField/AutoCompleteField';
import DateSelect from '../UI/DateSelect/DateSelect'
import DateDisplay from '../UI/DateDisplay/DateDisplay';
import ModalDialog from '../UI/Dialog/ModalDialog';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import common from '../../jsons/common.json';
import { useNavigate } from 'react-router-dom';

const NotificationDialog = (props) => {
    const [list, setList] = useState([]);
    const [offset, setOffset] = useState(0);
    const [listSize, setListSize] = useState(10);
    const [status, setStatus] = useState('');
    const [statuses, setStatuses] = useState([]);
    const [date, setDate] = useState('');
    const [activity, setActivity] = useState('');
    const [activities, setActivities] = useState([]);
    const [orderBy, setOrderBy] = useState('date');
    const [ordDir, setOrdDir] = useState(-1);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [statusSel, setStatusSel] = useState(null);
    const [actSel, setActSel] = useState(null);
    const { t } = useTranslation('translation');
    const navigate = useNavigate();

    const loadRecords = useCallback(async () => {
        const sort = { [orderBy]: ordDir };
        const data = {
            size: listSize,
            offset,
            date,
            status,
            activity,
            sort: JSON.stringify(sort)
        };
        try {
            const listResponse = await axios.get('/api/notifications', { params: data });
            setList(listResponse.data.data);
            setTotalCount(listResponse.data.totalCount);

        } catch (err) {
            props.dispatch({ type: 'CREATE_ALERT', payload: { message: err.response.data, title: t('error'), severity: 'error' } });
        }
    }, [orderBy, ordDir, listSize, offset, date, status, activity, t, props]);

    useEffect(() => {
        const fetchData = async () => {
            const values = await axios.get('/api/notificationlistvalues');
            setStatuses(values.data.statuses);
            setActivities(values.data.activities);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (statuses.length > 0 && activities.length > 0) {
            loadRecords();
        }
    }, [statuses, activities, loadRecords]);

    const changeAuto = (name) => (e, newValue) => {
        if (name === 'statusSel') {
            setStatusSel(newValue);
            setStatus(newValue != null ? newValue.value : null);
        } else if (name === 'actSel') {
            setActSel(newValue);
            setActivity(newValue != null ? newValue.value : null);
        }
        setPage(0);
        setOffset(0);
    };

    const dateChange = (e) => {
        setDate(e);
    };

    const sortClick = (name) => {
        if (orderBy === name) {
            setOrdDir(ordDir > 0 ? -1 : 1);
        } else {
            setOrderBy(name);
            setOrdDir(-1);
        }
        setOffset(0);
        setPage(0);
    };

    const handleRowsPerPage = (e) => {
        setListSize(parseInt(e.target.value));
        setPage(0);
        setOffset(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        setOffset(newPage * listSize);
    };
    // Status ids are minted per tenant, so a list that now spans tenants cannot compare against
    // the signed-in tenant's constants. The server sends a statusKey on every row and accepts
    // one back; see helpers/notificationScope.js.
    const nextStatusKey = (record) =>
        record.statusKey === 'new' || record.statusKey === 'unread' ? 'read' : 'unread';

    const clickMessage = (i, link) => {
        const record = list[i];
        // Rows from another tenant are shown but not navigable — the link resolves against that
        // tenant's data. Switch tenants from the profile menu to act on one.
        if (record.foreign) return;
        if (record.statusKey !== 'read') {
            axios.post('/api/notificationstatus/' + record._id, { statusKey: nextStatusKey(record) })
                .then(() => {
                    navigate(link);
                })
                .catch(err => {
                    props.dispatch({ type: 'CREATE_ALERT', payload: { message: err.response.data, title: t('error'), severity: 'error' } });
                });
        } else {
            navigate(link);
        }
        props.toggleDialog();
    };

    // Deliberately still available on foreign rows: they count toward the badge, and without a
    // way to mark them read from here the badge would stay lit until the user switched tenants.
    const changeStatus = (i) => {
        const record = list[i];
        axios.post('/api/notificationstatus/' + record._id, { statusKey: nextStatusKey(record) })
            .then(() => {
                loadRecords();
            })
            .catch(err => {
                props.dispatch({ type: 'CREATE_ALERT', payload: { message: err.response.data, title: t('error'), severity: 'error' } });
            });
    };

    return (
        <ModalDialog title={t('notifications')} toggleDialog={() => props.toggleDialog()}
            dialogStatus={open} fullWidth maxWidth={'xl'} fScreen={'md'}>
            <Grid container spacing={3}>
                <Grid
                    size={{
                        xs: 12,
                        lg: 3
                    }}>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 3,
                                lg: 12
                            }}>
                            <DateSelect
                                onChange={dateChange}
                                value={date}
                                label={t('date')}
                                format={props.auth.user.dateFormat}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 4,
                                lg: 12
                            }}>
                            <AutoCompleteField
                                value={statusSel}
                                options={statuses}
                                onChange={changeAuto('statusSel', 'status')}
                                label={t('status')}
                            />
                        </Grid>
                        <Grid
                            size={{
                                xs: 4,
                                lg: 12
                            }}>
                            <AutoCompleteField
                                value={actSel}
                                options={activities}
                                onChange={changeAuto('actSel', 'activity')}
                                label={t('activity')}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={3}
                    size={{
                        xs: 12,
                        lg: 9
                    }}>
                    <TableContainer component={Paper}>
                        <Table aria-label="Notifications">
                            <TableHead>
                                <TableRow key="tableHeader">
                                    <TableCell>
                                        <TableSortLabel onClick={() => sortClick('message')}
                                            active={orderBy === 'message'} direction={ordDir > 0 ? 'asc' : 'desc'}>{t('message')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel onClick={() => sortClick('date')}
                                            active={orderBy === 'date'} direction={ordDir > 0 ? 'asc' : 'desc'}>{t('date')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel onClick={() => sortClick('status')}
                                            active={orderBy === 'status'} direction={ordDir > 0 ? 'asc' : 'desc'}>{t('status')}
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell>
                                        <TableSortLabel onClick={() => sortClick('relatedObject')}
                                            active={orderBy === 'relatedObject'} direction={ordDir > 0 ? 'asc' : 'desc'}>{t('activity')}
                                        </TableSortLabel>
                                    </TableCell>
                                    {/* Only worth a column when the user actually belongs to more than one
                                        tenant — for everyone else every row would read the same. */}
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {list.map((row, i) =>
                                    <TableRow key={row._id}>
                                        <TableCell>
                                            {/* Bold means unread. This was previously unconditional, which made
                                                read and unread rows identical in the one view built for managing
                                                them. Matches the bell dropdown (AppBars/Buttons/Notifications.jsx).
                                                Foreign rows render as plain text — they carry no link, so a Link
                                                would advertise navigation that cannot happen. */}
                                            {row.foreign ?
                                                <Typography component="span" sx={{
                                                    fontStyle: (row.statusKey === 'unread' ? 'italic' : null),
                                                    fontWeight: (row.statusKey === 'new' || row.statusKey === 'unread' ? 'bold' : null)
                                                }}>
                                                    {row.message}
                                                </Typography> :
                                                <Link onClick={() => clickMessage(i, row.link)}
                                                    sx={{
                                                        fontStyle: (row.statusKey === 'unread' ? 'italic' : null),
                                                        fontWeight: (row.statusKey === 'new' || row.statusKey === 'unread' ? 'bold' : null),
                                                        cursor: 'pointer'
                                                    }}>
                                                    {row.message}
                                                </Link>
                                            }
                                            {/* Which tenant the notification came from, as subtext under the
                                                message — it is what explains why the row is not clickable. */}
                                            {row.foreign &&
                                                <Typography color="primary" variant="subtitle2" component="div"
                                                            title={t('notifOtherTenant')}>
                                                    {row.tenantName != null ? row.tenantName : t('otherTenant')}
                                                </Typography>
                                            }
                                        </TableCell>
                                        <TableCell><DateDisplay value={row.date} format={props.auth.user.dateFormat} /></TableCell>
                                        <TableCell>{row.status != null ? row.status.label : ''}</TableCell>
                                        <TableCell>{row.relatedObject != null ? row.relatedObject.text : ''}</TableCell>
                                        <TableCell>
                                            {row.status != null &&
                                                <Button variant="outlined" color="primary" onClick={() => changeStatus(i)}>
                                                    {row.statusKey === 'new' || row.statusKey === 'unread' ?
                                                        t('markRead') : t('markUnread')}
                                                </Button>
                                            }
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow key="tableFooter">
                                    <TablePagination count={totalCount}
                                        rowsPerPage={listSize}
                                        onPageChange={handleChangePage}
                                        page={page}
                                        rowsPerPageOptions={common.rowOptions}
                                        onRowsPerPageChange={handleRowsPerPage} />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </ModalDialog>
    );
};
const mapStateToProps = state => ({
    auth: state.auth,
    notification: state.notifications,
    profile: state.profile
});


const NotificationDialogConnected = connect(mapStateToProps, { setNotificationCount })(NotificationDialog);

export default NotificationDialogConnected;
